import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { toast } from "sonner";
import { Database } from "@/integrations/supabase/types";

type AppRole = Database["public"]["Enums"]["app_role"];

export interface TeamMember {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  role: AppRole;
}

export function useTeamMembers() {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ["team-members", profile?.tenant_id],
    queryFn: async () => {
      if (!profile?.tenant_id) return [];

      // Get all profiles in the same tenant
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("id, email, first_name, last_name, avatar_url")
        .eq("tenant_id", profile.tenant_id);

      if (profilesError) throw profilesError;

      // Get roles for each user
      const userIds = profiles.map((p) => p.id);
      const { data: roles, error: rolesError } = await supabase
        .from("user_roles")
        .select("user_id, role")
        .in("user_id", userIds);

      if (rolesError) throw rolesError;

      // Combine profiles with roles
      const members: TeamMember[] = profiles.map((p) => {
        const userRole = roles?.find((r) => r.user_id === p.id);
        return {
          ...p,
          role: (userRole?.role || "sales_agent") as AppRole,
        };
      });

      return members;
    },
    enabled: !!profile?.tenant_id,
  });
}

export function useUpdateMemberRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: AppRole }) => {
      // Check if role exists
      const { data: existingRole, error: checkError } = await supabase
        .from("user_roles")
        .select("id")
        .eq("user_id", userId)
        .maybeSingle();

      if (checkError) throw checkError;

      if (existingRole) {
        // Update existing role
        const { error } = await supabase
          .from("user_roles")
          .update({ role })
          .eq("user_id", userId);

        if (error) throw error;
      } else {
        // Insert new role
        const { error } = await supabase
          .from("user_roles")
          .insert([{ user_id: userId, role }]);

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["team-members"] });
      toast.success("Role updated successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to update role: ${error.message}`);
    },
  });
}

export function useInviteTeamMember() {
  const { profile } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ email, role }: { email: string; role: string }) => {
      if (!profile?.tenant_id) throw new Error("No tenant");

      // For now, we'll create an invite record or send an email
      // In a full implementation, this would send an invitation email
      const { data, error } = await supabase.functions.invoke("send-notification", {
        body: {
          type: "team_invite",
          userId: profile.id,
          data: {
            invitedEmail: email,
            role,
            inviterName: `${profile.first_name || ""} ${profile.last_name || ""}`.trim(),
            appUrl: window.location.origin,
          },
        },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["team-members"] });
      toast.success("Invitation sent successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to send invitation: ${error.message}`);
    },
  });
}

export function useSendNotification() {
  return useMutation({
    mutationFn: async ({
      type,
      userId,
      data,
    }: {
      type: "new_lead" | "deal_stage_change" | "scheduled_call_reminder" | "hot_lead_alert";
      userId: string;
      data: Record<string, any>;
    }) => {
      const { data: response, error } = await supabase.functions.invoke(
        "send-notification",
        {
          body: { type, userId, data: { ...data, appUrl: window.location.origin } },
        }
      );

      if (error) throw error;
      return response;
    },
    onError: (error: Error) => {
      console.error("Failed to send notification:", error);
    },
  });
}
